import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuditLogs, deleteAdmin } from "../../utils/backend";
import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CInputGroup,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CIcon from "@coreui/icons-react";
import {
  cilSwapVertical,
  cilSortAlphaUp,
  cilSortAlphaDown,
  cilSortNumericUp,
  cilSortNumericDown,
} from "@coreui/icons";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";
import { setUppercase } from "../../utils/SetUppercase";

const AuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [admin_name, setAdminName] = useState("");
  const [role, setRole] = useState("");
  const [module_name, setModuleName] = useState("");
  const [action, setAction] = useState("");
  const [start_time, setStartTime] = useState("");
  const [end_time, setEndTime] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "desc",
  });

  useEffect(() => {
    // Load filters and pagination state from localStorage on page load
    const savedState = JSON.parse(localStorage.getItem("auditLogsState"));
    if (savedState) {
      setAdminName(savedState.admin_name || "");
      setRole(savedState.role || "");
      setModuleName(savedState.module_name || "");
      setAction(savedState.action || "");
      setStartTime(savedState.start_time || "");
      setEndTime(savedState.end_time || "");
      setPerPage(savedState.perPage || 10);

      // Fetch data based on the saved state
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      const filters = {
        admin_name: savedState.admin_name || "",
        role: savedState.role || "",
        module_name: savedState.module_name || "",
        action: savedState.action || "",
        start_time: savedState.start_time || "",
        end_time: savedState.end_time || "",
        page: 1,
        perPage: savedState.perPage || 10,
      };
      fetchData(filters);
    } else {
      fetchData();
    }
  }, []);

  const fetchData = async (filters = { perPage: perPage }) => {
    //setIsLoading(true);
    try {
      const response = await getAuditLogs(filters);
      setAuditLogs(setUppercase(response.data.data));
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAuditLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveStateToLocalStorage = () => {
    const existingState =
      JSON.parse(localStorage.getItem("auditLogsState")) || {};
    // Evict keys written by older builds; the merge below would otherwise keep a
    // stale currentPage alive in every admin's browser indefinitely.
    delete existingState.currentPage;
    const state = {
      admin_name,
      role,
      module_name,
      action,
      start_time,
      end_time,
    };
    localStorage.setItem(
      "auditLogsState",
      JSON.stringify({ ...existingState, ...state })
    );
  };

  const handleSearch = async (e) => {
    const filters = {
      admin_name,
      role,
      module_name,
      action,
      start_time,
      end_time,
      page: 1,
      perPage: perPage,
    };
    saveStateToLocalStorage(); // Save state before fetching data
    await fetchData(filters);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => clearTimeout(timeout);
  }, [admin_name, role, module_name, action, start_time, end_time, perPage]);

  const handleReset = () => {
    setAdminName("");
    setRole("");
    setModuleName("");
    setAction("");
    setStartTime("");
    setEndTime("");
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState =
      JSON.parse(localStorage.getItem("auditLogsState")) || {};
    localStorage.setItem(
      "auditLogsState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData();
  };

  const handlePageChange = async (page) => {
    const filters = {
      admin_name,
      role,
      module_name,
      action,
      start_time,
      end_time,
      page: page,
      perPage: perPage,
    };
    await fetchData(filters);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "auditLogsState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("auditLogsState")),
        perPage: newPerPage,
      })
    );
    const filters = {
      admin_name,
      role,
      module_name,
      action,
      start_time,
      end_time,
      page: 1,
      perPage: newPerPage,
    };
    await fetchData(filters);
  };

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc"; // Toggle back to ascending
    }
    setSortConfig({ key, direction });
    setAuditLogs((prevLogs) => [...prevLogs]); // Trigger re-render to apply styles
  };

  const sortedAuditLogs = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return auditLogs;
    return [...auditLogs].sort((a, b) => {
      const aValue = a[sortConfig.key]?.toString().toLowerCase() || "";
      const bValue = b[sortConfig.key]?.toString().toLowerCase() || "";
      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [auditLogs, sortConfig]);

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    admin_name,
    role,
    module_name,
    action,
    start_time,
    end_time,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <CRow>
                <CCol className="justify-content-end d-flex my-1 align-items-center">
                  {activeFilterCount > 0 && (
                    <span
                      className="me-3 text-nowrap"
                      style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffe69c",
                        borderRadius: "12px",
                        color: "#664d03",
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        padding: "4px 10px",
                      }}
                    >
                      <i className="bi bi-funnel-fill me-1"></i>
                      {activeFilterCount}{" "}
                      {activeFilterCount === 1 ? "filter" : "filters"} active —
                      showing a subset
                    </span>
                  )}
                  <CButton
                    className="ansa-button-secondary me-3"
                    type="button"
                    onClick={handleReset}
                  >
                    Reset
                  </CButton>
                </CCol>
              </CRow>
              <CTable striped borderless>
                <CTableHead>
                  <CTableRow>
                    <CTableDataCell></CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={admin_name}
                        onChange={(e) => setAdminName(e.target.value)}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={module_name}
                        onChange={(e) => setModuleName(e.target.value)}
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      <CFormInput
                        type="text"
                        placeholder="Search"
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                      />
                    </CTableDataCell>
                    <CTableDataCell></CTableDataCell>
                    <CInputGroup>
                      <CFormInput
                        type="date"
                        value={start_time}
                        onChange={(e) => setStartTime(e.target.value)}
                        title="Start Date"
                      />

                      <span className="input-group-text bg-light text-muted small">
                        to
                      </span>

                      <CFormInput
                        type="date"
                        value={end_time}
                        onChange={(e) => setEndTime(e.target.value)}
                        title="End Date"
                      />
                    </CInputGroup>
                  </CTableRow>
                  <CTableRow style={{ height: "60px" }}>
                    <CTableHeaderCell
                      onClick={() => {}}
                      className={`hover-header ${
                        sortConfig.key === "id" ? "ctable-active-header" : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "80px",
                      }}
                    >
                      No.{" "}
                      {sortConfig.key === "id" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortNumericDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortNumericUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("action")}
                      className={`hover-header ${
                        sortConfig.key === "action"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "150px",
                      }}
                    >
                      Action{" "}
                      {sortConfig.key === "action" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("admin_name")}
                      className={`hover-header ${
                        sortConfig.key === "admin_name"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "150px",
                      }}
                    >
                      Username{" "}
                      {sortConfig.key === "admin_name" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("role")}
                      className={`hover-header ${
                        sortConfig.key === "role" ? "ctable-active-header" : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "150px",
                      }}
                    >
                      Role{" "}
                      {sortConfig.key === "role" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("module_name")}
                      className={`hover-header ${
                        sortConfig.key === "module_name"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "150px",
                      }}
                    >
                      Module{" "}
                      {sortConfig.key === "module_name" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>

                    <CTableHeaderCell
                      onClick={() => handleSort("description")}
                      className={`hover-header ${
                        sortConfig.key === "description"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "200px",
                      }}
                    >
                      Description{" "}
                      {sortConfig.key === "description" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortAlphaDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortAlphaUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>
                    <CTableHeaderCell
                      onClick={() => handleSort("action_time")}
                      className={`hover-header ${
                        sortConfig.key === "action_time"
                          ? "ctable-active-header"
                          : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        fontSize: "1rem",
                        whiteSpace: "nowrap",
                        minWidth: "150px",
                      }}
                    >
                      Created At{" "}
                      {sortConfig.key === "action_time" ? (
                        sortConfig.direction === "asc" ? (
                          <CIcon
                            icon={cilSortNumericDown}
                            size="lg"
                            className="float-end"
                          />
                        ) : (
                          <CIcon
                            icon={cilSortNumericUp}
                            size="lg"
                            className="float-end"
                          />
                        )
                      ) : (
                        <CIcon
                          icon={cilSwapVertical}
                          size="lg"
                          className="float-end text-muted"
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {sortedAuditLogs.map((user, index) => (
                    <CTableRow key={user.id} style={{ height: "60px" }}>
                      <CTableDataCell>
                        {index + 1 + perPage * (currentPage - 1)}
                      </CTableDataCell>
                      <CTableDataCell>{user.action || "N/A"}</CTableDataCell>
                      <CTableDataCell>
                        {user.admin_name || "N/A"}
                      </CTableDataCell>

                      <CTableDataCell>{user.role || "N/A"}</CTableDataCell>
                      <CTableDataCell>
                        {user.module_name || "N/A"}
                      </CTableDataCell>

                      <CTableDataCell>
                        {user.description || "N/A"}
                      </CTableDataCell>

                      <CTableDataCell>
                        {user.action_time
                          ? formatDateTime(user.action_time)
                          : "N/A"}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(
                      {
                        admin_name,
                        role,
                        module_name,
                        action,
                        start_time,
                        end_time,
                        perPage: perPage,
                      },
                      page
                    );
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 || // First page
                      pageNumber === totalPages - 1 || // Last page
                      pageNumber < 5 || // First few pages
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2) // Pages around the current page
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) || // Ellipsis after the first few pages
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4) // Ellipsis before the last page
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {auditLogs.length} of {totalItems} logs
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
    </>
  );
};

export default AuditLogs;
